import { ChangeEvent, SelectHTMLAttributes, useEffect, useState, useRef } from "react";
import { ArrowDown } from "components";
import { twMerge } from "tailwind-merge";

export type OptionProps = {
  value: string | number;
  label?: string;
  disabled?: boolean;
};

export interface SearchableSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options: OptionProps[];
  placeholder?: string;
}

export const SearchableSelect = ({
  options,
  placeholder = "Choose an option",
  value,
  required = true,
  onChange = () => null,
  disabled = false,
  ...rest
}: SearchableSelectProps) => {
  const [selectedValue, setSelectedValue] = useState<string | number | readonly string[]>(value || "");
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedValue(value || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOnChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;
    setSelectedValue(newValue);
    onChange(event);
    setIsOpen(false);
    setSearch("");
  };

  const handleOptionClick = (value: string | number) => {
    setSelectedValue(value);
    onChange({ target: { value: String(value) } } as ChangeEvent<HTMLSelectElement>);
    setIsOpen(false);
    setSearch("");
  };

  const filteredOptions = options.filter((option) => option.label?.toLowerCase().includes(search.toLowerCase()));

  // Find the currently selected option
  const selectedOption = options.find((opt) => String(opt.value) === String(selectedValue));

  return (
    <div ref={wrapperRef} className={twMerge("relative flex w-full flex-row", !disabled && "cursor-pointer")}>
      <select
        className="hidden"
        value={selectedValue}
        onChange={handleOnChange}
        required={required}
        disabled={disabled}
        {...rest}
      >
        {!!placeholder && <option value="">{placeholder}</option>}
        {options.map(({ value, label }, index) => (
          <option key={index} value={value}>
            {label}
          </option>
        ))}
      </select>

      <div
        className={twMerge(
          "w-full appearance-none border border-primary py-1.5 pl-2 pr-10 text-center ring-primary focus:border-primary focus:ring-0",
          !disabled && "cursor-pointer",
          !selectedOption && "text-primary"
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        role="combobox"
        aria-expanded={isOpen ? "true" : "false"}
        aria-haspopup="listbox"
        aria-activedescendant={String(selectedValue)}
      >
        {selectedOption ? selectedOption.label : placeholder}
      </div>

      <span className="pointer-events-none absolute inset-y-0 right-0 flex w-8 items-center justify-center bg-primary">
        <ArrowDown />
      </span>

      {isOpen && (
        <div className="absolute left-0 top-full z-10 mt-1 max-h-60 w-full overflow-y-auto border border-primary bg-white shadow-lg">
          <div className="sticky bottom-0 border-t border-primary bg-white p-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full border border-primary px-2 py-1 text-sm focus:border-primary focus:outline-none"
            />
          </div>
          {filteredOptions.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-gray-500">No options available</div>
          ) : (
            filteredOptions.map(({ value, label }, index) => (
              <div
                key={index}
                className="cursor-pointer px-2 py-1.5 hover:bg-primary/10"
                onClick={() => handleOptionClick(value)}
              >
                {label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
